import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ApiError, json, post, vendedores } from "../api/client";
import { guardarSesion, leerSesion } from "../api/sesion";
import type { Vendedor } from "../domain/types";
import { Campo, ErrorAviso, Modal } from "./ui";
import Icono from "./Icono";
import { useAvisos } from "./Avisos";
export const SESSION_KEY = "cotizador_vendedor";
// Vendedor con sesión vigente, o null. Sin sesión no hay vendedor: así una
// sesión vencida (o de antes de que existieran) lleva al login.
export function leerVendedor(): Vendedor | null {
  if (!leerSesion()) {
    // El nombre guardado sin sesión (sesión vencida, o de antes de que
    // existieran) se borra: si no, las llamadas lo verían como "logueado".
    try {
      localStorage.removeItem(SESSION_KEY);
    } catch {
      // nada que borrar
    }
    return null;
  }
  try {
    const v = JSON.parse(localStorage.getItem(SESSION_KEY) || "null");
    return v &&
      typeof v.id === "string" &&
      typeof v.nombre === "string" &&
      ["admin", "vendedor"].includes(v.rol)
      ? v
      : null;
  } catch {
    return null;
  }
}
export function Login({ onLogin }: { onLogin: (v: Vendedor) => void }) {
  const list = useQuery({
    queryKey: ["vendedores"],
    queryFn: ({ signal }) => vendedores(signal),
  });
  const [id, setId] = useState(""),
    [pin, setPin] = useState(""),
    [busy, setBusy] = useState(false),
    [error, setError] = useState<unknown>();
  return (
    <main className="login">
      <img
        src="/assets/logo-diez-visual-oscuro.svg"
        alt="Diez Visual"
        className="logo logo-dark"
      />
      <img
        src="/assets/logo-diez-visual.svg"
        alt="Diez Visual"
        className="logo logo-light"
      />
      <div className="card">
        <p className="eyebrow">Cotizador comercial</p>
        <h1>¿Quién va a cotizar?</h1>
        <p>Selecciona tu nombre para continuar.</p>
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            setBusy(true);
            setError(null);
            try {
              const r = await post<{
                ok: boolean;
                vendedor: Vendedor;
                sesion?: string;
              }>("vendedores/validar", { id, pin });
              if (!r.ok || !r.vendedor?.id)
                throw new Error("El PIN no es correcto.");
              // Sin sesión el resto de las llamadas fallarían: un backend
              // anterior a las sesiones no la entrega, y eso se dice claro.
              if (!r.sesion)
                throw new Error(
                  "El servidor no entregó una sesión. Avísale a un administrador.",
                );
              guardarSesion(r.sesion);
              localStorage.setItem(SESSION_KEY, JSON.stringify(r.vendedor));
              onLogin(r.vendedor);
            } catch (e) {
              setError(
                e instanceof ApiError && e.status === 401
                  ? new Error("El PIN no es correcto.")
                  : e,
              );
            } finally {
              setBusy(false);
            }
          }}
        >
          <Campo label="Vendedor">
            <select required value={id} onChange={(e) => setId(e.target.value)}>
              <option value="">Selecciona tu nombre</option>
              {list.data?.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.nombre}
                </option>
              ))}
            </select>
          </Campo>
          <Campo label="PIN (4 dígitos)">
            <input
              required
              type="password"
              inputMode="numeric"
              pattern="[0-9]{4}"
              title="deben ser 4 dígitos numéricos"
              maxLength={4}
              autoComplete="current-password"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
            />
          </Campo>
          <ErrorAviso error={error || list.error} />
          {list.isPending ? <p role="status">Cargando vendedores…</p> : null}
          {list.isError ? (
            <button type="button" onClick={() => void list.refetch()}>
              <Icono nombre="historial" /> Reintentar
            </button>
          ) : null}
          <button
            className="primary"
            disabled={busy || !list.data}
            type="submit"
          >
            {busy ? "Validando…" : "Entrar"}
            <Icono nombre={busy ? "cargar" : "siguiente"} />
          </button>
        </form>
      </div>
    </main>
  );
}
